import React, { createContext, useContext, useState, useEffect } from 'react';

interface Toast {
  show: boolean;
  message: string;
  status: 'syncing' | 'success' | 'error' | 'warning';
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, status: Toast['status'], duration?: number) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<Toast>({ show: false, message: '', status: 'syncing', duration: 3500 });

  useEffect(() => {
    if (toast.show && toast.status !== 'syncing') {
      const timer = setTimeout(() => setToast({ show: false, message: '', status: 'syncing', duration: 3500 }), toast.duration || 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message: string, status: Toast['status'], duration?: number) => {
    setToast({ show: true, message, status, duration: duration || 3500 });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', status: 'syncing' });
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toast.show && (
        <div className={`toast-notification toast-${toast.status}`}>
          <span className="toast-icon">
            {toast.status === 'success' && '✔'}
            {toast.status === 'error' && '✖'}
            {toast.status === 'syncing' && '⏳'}
            {toast.status === 'warning' && '⚠'}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};